import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@eriko/ui/src/components/ui/dialog';
import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import { Textarea } from '@eriko/ui/src/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useReconciliationStore } from '../../stores/reconciliation';
import { useCreateRule } from '../../hooks/useReconciliation';
import type { CreateRuleBody, CreateRuleDryRunResponse } from '../../api/reconciliation';
import { formatCurrency } from '@eriko/shared-types';

const RULE_TYPES: CreateRuleBody['ruleType'][] = [
  'MERCHANT_CATEGORY',
  'MERCHANT_ACCOUNT',
  'RECONCILIATION',
  'PATTERN',
  'TRANSFER',
  'PAYOUT',
];

interface Props {
  orgId: string;
}

/**
 * Outer wrapper subscribes to the store and only renders the form when open.
 * Mounting/unmounting handles state reset naturally (no setState-in-effect).
 * Prefill is consumed via useState initializers, so the form picks up the
 * latest prefill each time the dialog opens.
 */
export function CreateRuleDialog({ orgId }: Props) {
  const open = useReconciliationStore((s) => s.createRuleOpen);
  const prefill = useReconciliationStore((s) => s.createRulePrefill);
  const close = useReconciliationStore((s) => s.closeCreateRule);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-lg">
        {open && (
          <CreateRuleForm
            orgId={orgId}
            prefill={prefill}
            onClose={close}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface FormProps {
  orgId: string;
  prefill: { merchantId?: string; patternText?: string; categoryId?: string } | null;
  onClose: () => void;
}

function CreateRuleForm({ orgId, prefill, onClose }: FormProps) {
  const createRule = useCreateRule(orgId);

  const [ruleType, setRuleType] = useState<CreateRuleBody['ruleType']>('MERCHANT_CATEGORY');
  const [merchantId, setMerchantId] = useState(prefill?.merchantId ?? '');
  const [patternText, setPatternText] = useState(prefill?.patternText ?? '');
  const [categoryId, setCategoryId] = useState(prefill?.categoryId ?? '');
  const [accountCode, setAccountCode] = useState('');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<CreateRuleDryRunResponse | null>(null);

  const baseBody: CreateRuleBody = {
    ruleType,
    merchantId: merchantId || undefined,
    patternText: patternText || undefined,
    categoryId: categoryId || undefined,
    accountCode: accountCode || undefined,
    notes: notes || undefined,
  };

  const canSubmit = !!(merchantId || patternText);

  const handlePreview = async () => {
    if (!canSubmit) return;
    const result = await createRule.mutateAsync({ ...baseBody, dryRun: true });
    setPreview(result as CreateRuleDryRunResponse);
  };

  const handlePersist = async () => {
    if (!canSubmit) return;
    await createRule.mutateAsync({ ...baseBody, dryRun: false });
    onClose();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Create reconciliation rule</DialogTitle>
        <DialogDescription>
          Teach the engine to handle similar transactions consistently. Preview the impact,
          then persist.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label>Rule type</Label>
          <Select
            value={ruleType}
            onValueChange={(v) => setRuleType(v as CreateRuleBody['ruleType'])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RULE_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t.replace(/_/g, ' ').toLowerCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="merchant-id">Merchant id (optional)</Label>
          <Input
            id="merchant-id"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
            placeholder="merch_..."
          />
        </div>

        <div>
          <Label htmlFor="pattern-text">Pattern (substring, case-insensitive)</Label>
          <Input
            id="pattern-text"
            value={patternText}
            onChange={(e) => setPatternText(e.target.value)}
            placeholder="AWS, STRIPE PAYOUT, etc."
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="category-id">Category id</Label>
            <Input
              id="category-id"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              placeholder="cat_..."
            />
          </div>
          <div>
            <Label htmlFor="account-code">Account code</Label>
            <Input
              id="account-code"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              placeholder="e.g. tax-liability"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="rule-notes">Notes (optional)</Label>
          <Textarea
            id="rule-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={1000}
            className="min-h-[60px]"
          />
        </div>

        {preview && (
          <div className="rounded-md border border-surface-200 bg-surface-50 p-3 text-sm space-y-2">
            <div className="font-medium text-surface-900">
              Preview: this rule will apply to{' '}
              <span className="text-surface-900 font-bold">{preview.willApplyToCount}</span>{' '}
              existing transactions.
            </div>
            {preview.sampleTransactions.length > 0 && (
              <ul className="space-y-1">
                {preview.sampleTransactions.map((t) => (
                  <li
                    key={t.id}
                    className="flex justify-between text-xs text-surface-700"
                  >
                    <span className="truncate">
                      {t.description || t.rawDescription || t.id}
                    </span>
                    <span className="font-mono ml-2">
                      {formatCurrency(Math.abs(parseFloat(t.amount)), 2, t.currency ?? undefined)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        {!preview ? (
          <Button onClick={handlePreview} disabled={!canSubmit || createRule.isPending}>
            {createRule.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Previewing…
              </>
            ) : (
              'Preview'
            )}
          </Button>
        ) : (
          <Button onClick={handlePersist} disabled={createRule.isPending}>
            {createRule.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving…
              </>
            ) : (
              'Create rule'
            )}
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
